import { Box, Button, Flex, Tabs, Text, TextInput } from "@mantine/core";
import { MagnifyingGlassIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import classes from "./list.module.css";

/** Mantine Tabs rejects empty string values — map "" ↔ this sentinel. */
const ALL_TAB = "__all__";

function toTabValue(value: string): string {
  return value === "" ? ALL_TAB : value;
}

function fromTabValue(value: string): string {
  return value === ALL_TAB ? "" : value;
}

export type ToolbarTab = {
  value: string;
  label: string;
  count?: number;
  /** `danger` paints the tab and its count red (e.g. overdue). */
  tone?: "danger";
};

export type FilterChip = {
  key: string;
  label: string;
  active: boolean;
  onClick: () => void;
  onClear?: () => void;
};

type SearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  w?: number | string;
};

type Props = {
  left?: ReactNode;
  right?: ReactNode;
  search?: SearchProps;
  clearFilters?: () => void;
  hasActiveFilters?: boolean;
  tabs?: ToolbarTab[];
  activeTab?: string;
  onTabChange?: (value: string) => void;
  chips?: FilterChip[];
  filterSlot?: ReactNode;
};

export function ListToolbar({
  left,
  right,
  search,
  clearFilters,
  hasActiveFilters,
  tabs,
  activeTab,
  onTabChange,
  chips,
  filterSlot,
}: Props) {
  const { t } = useTranslation("common");

  const searchNode = search ? (
    <TextInput
      value={search.value}
      onChange={(e) => search.onChange(e.currentTarget.value)}
      placeholder={search.placeholder ?? t("search")}
      w={search.w ?? 280}
      size="sm"
      radius="md"
      leftSection={<MagnifyingGlassIcon size={16} />}
    />
  ) : null;

  const clearNode =
    hasActiveFilters && clearFilters ? (
      <Button variant="subtle" color="gray" size="sm" leftSection={<XIcon size={14} />} onClick={clearFilters}>
        {t("list.clearFilters")}
      </Button>
    ) : null;

  const hasLegacy = !!(left || searchNode || clearNode);
  const hasTabs = !!(tabs && tabs.length > 0);
  const chipNodes = (
    <>
      {chips?.map((chip) =>
        chip.active ? (
          <Box
            key={chip.key}
            component="button"
            type="button"
            className={`${classes.filterChip} ${classes.filterChipActive}`}
            onClick={chip.onClear ?? chip.onClick}
          >
            <Text span size="sm" fw={500}>
              {chip.label}
            </Text>
            <XIcon size={12} weight="bold" />
          </Box>
        ) : (
          <Box
            key={chip.key}
            component="button"
            type="button"
            className={classes.filterChip}
            onClick={chip.onClick}
          >
            <PlusIcon size={12} weight="bold" />
            <Text span size="sm">
              {chip.label || t("list.addFilter")}
            </Text>
          </Box>
        ),
      )}
      {filterSlot}
    </>
  );
  const hasChips = !!(chips && chips.length > 0) || !!filterSlot;

  return (
    <Box className={classes.toolbar}>
      <Flex className={classes.toolbarRow}>
        {hasTabs && (
          <Tabs
            value={toTabValue(activeTab ?? tabs![0]?.value ?? "")}
            onChange={(v) => v != null && onTabChange?.(fromTabValue(v))}
            variant="default"
            className={classes.toolbarTabs}
            // @dehub/theme Tabs-д inline padding (sm/lg) өгдөг тул CSS module дийлдэггүй.
            styles={{ tab: { padding: 0 } }}
          >
            <Tabs.List>
              {tabs!.map((tab) => (
                <Tabs.Tab
                  key={toTabValue(tab.value)}
                  value={toTabValue(tab.value)}
                  data-tone={tab.tone}
                  rightSection={
                    typeof tab.count === "number" ? (
                      <Text component="span" className={classes.tabCount} data-tone={tab.tone}>
                        {tab.count}
                      </Text>
                    ) : undefined
                  }
                >
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        )}
        {hasLegacy && (
          <Flex className={classes.toolbarSide} align="center" gap="sm" wrap="wrap">
            {left}
            {searchNode}
            {clearNode}
          </Flex>
        )}
        {(hasChips || right) && (
          <Box className={classes.toolbarRight}>
            {hasChips && chipNodes}
            {right}
          </Box>
        )}
      </Flex>
    </Box>
  );
}
